import { useEffect, useState } from 'react';
import clsx from 'clsx';
import {
  ACTION_DESCRIPTION_MAX_LENGTH,
  ACTION_TITLE_MAX_LENGTH,
  type ActionItemView,
  type AssignableMemberView,
  type CommentView,
  type TopicView,
} from '@retro/shared';
import { useRetroStore } from '../../lib/retroStore.js';
import { Avatar, Modal } from '../ui.js';

const SMART_HINT = [
  '**S**pecific — what exactly changes?',
  '**M**easurable — how will we know it happened?',
  '**A**chievable — can we do it in one sprint?',
  '**R**elevant — does it address a real pain?',
  '**T**ime-bound — by when?',
];

/** Local date in the yyyy-mm-dd form a native date input expects. */
function toDateInput(iso: string | null): string {
  if (!iso) return '';
  const date = new Date(iso);
  const offset = date.getTimezoneOffset() * 60_000;
  return new Date(date.getTime() - offset).toISOString().slice(0, 10);
}

export function ActionItemDialog({
  open,
  onClose,
  existing,
  members,
  topics,
  comments,
  initialLinkedCommentIds = [],
}: {
  open: boolean;
  onClose: () => void;
  existing: ActionItemView | null;
  members: AssignableMemberView[];
  topics: TopicView[];
  comments: CommentView[];
  initialLinkedCommentIds?: string[];
}) {
  const send = useRetroStore((state) => state.send);

  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [assignee, setAssignee] = useState<string>('');
  const [dueDate, setDueDate] = useState('');
  const [topicIds, setTopicIds] = useState<string[]>([]);
  const [linkedIds, setLinkedIds] = useState<string[]>([]);
  const [showSmart, setShowSmart] = useState(false);
  const [showLinks, setShowLinks] = useState(false);
  const [saving, setSaving] = useState(false);

  // Reset the form whenever the dialog opens, so a previous edit cannot bleed
  // into the next one.
  useEffect(() => {
    if (!open) return;
    setTitle(existing?.title ?? '');
    setDescription(existing?.description ?? '');
    setAssignee(existing?.assignee?.userId ?? '');
    setDueDate(toDateInput(existing?.dueDate ?? null));
    setTopicIds(existing?.topicIds ?? []);
    setLinkedIds(existing?.linkedCommentIds ?? initialLinkedCommentIds);
    setShowSmart(false);
    setShowLinks(false);
  }, [open, existing, initialLinkedCommentIds]);

  async function save() {
    if (!title.trim() || saving) return;
    setSaving(true);

    const result = await send('action:upsert', {
      actionItemId: existing?.id ?? null,
      title: title.trim(),
      description: description.trim(),
      assigneeUserId: assignee || null,
      // A date input has no time; anchor to midday to dodge timezone rollovers.
      dueDate: dueDate ? new Date(`${dueDate}T12:00:00`).toISOString() : null,
      topicIds,
      linkedCommentIds: linkedIds,
    });

    setSaving(false);
    if (result !== null) onClose();
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={existing ? 'Edit action item' : 'New action item'}
      footer={
        <>
          {existing ? (
            <button
              type="button"
              className="btn btn-danger-text mr-auto"
              onClick={() => {
                void send('action:delete', { actionItemId: existing.id });
                onClose();
              }}
            >
              Delete
            </button>
          ) : null}
          <button type="button" className="btn" onClick={onClose}>
            Cancel
          </button>
          <button type="button" className="btn btn-primary" onClick={() => void save()} disabled={!title.trim() || saving}>
            {saving ? 'Saving…' : 'Save action'}
          </button>
        </>
      }
    >
      <div className="space-y-4">
        <label className="field-l">
            Action item name
          <input
            className="input"
            autoFocus
            value={title}
            maxLength={ACTION_TITLE_MAX_LENGTH}
            onChange={(event) => setTitle(event.target.value)}
            placeholder="Add a pairing rota to the sprint board"
          />
        </label>

        <label className="field-l">
            Description
          <textarea
            className="input min-h-[6rem] resize-y"
            value={description}
            maxLength={ACTION_DESCRIPTION_MAX_LENGTH}
            onChange={(event) => setDescription(event.target.value)}
            placeholder="What will change, and how we'll know it worked."
          />
          <button
            type="button"
            className="mt-1 text-xs font-medium text-brand-600 hover:underline"
            onClick={() => setShowSmart((value) => !value)}
          >
            {showSmart ? 'Hide the SMART framework' : 'Use the SMART framework'}
          </button>
          {showSmart ? (
            <ul className="mt-2 space-y-1 rounded-xl bg-brand-50 p-3 text-xs text-ink/75">
              {SMART_HINT.map((line) => (
                <li key={line}>{line.replace(/\*\*/g, '')}</li>
              ))}
            </ul>
          ) : null}
        </label>

        <div className="grid gap-4 sm:grid-cols-2">
          <div>
            <label className="field-l">
            Owner
              <select
                className="input"
                value={assignee}
                onChange={(event) => setAssignee(event.target.value)}
                aria-describedby="action-owner-hint"
              >
                <option value="">Nobody yet</option>
                {members.map((member) => (
                  <option key={member.userId} value={member.userId}>
                    {member.name}
                  </option>
                ))}
              </select>
            </label>
            <p id="action-owner-hint" className="mt-1 text-xs text-ink/50">
              One owner keeps accountability clear — they report progress, not do it all.
            </p>
          </div>

          <label className="field-l">
            Due date
            <div className="flex gap-1">
              <input
                type="date"
                className="input"
                value={dueDate}
                onChange={(event) => setDueDate(event.target.value)}
              />
              {dueDate ? (
                <button type="button" className="btn shrink-0" onClick={() => setDueDate('')} aria-label="Clear due date">
                  ✕
                </button>
              ) : null}
            </div>
          </label>
        </div>

        <div>
          <span className="block text-[13px] font-semibold">Topics</span>
          <div className="flex flex-wrap gap-1.5">
            {topics.map((topic) => {
              const selected = topicIds.includes(topic.id);
              return (
                <button
                  key={topic.id}
                  type="button"
                  onClick={() =>
                    setTopicIds((current) =>
                      selected ? current.filter((id) => id !== topic.id) : [...current, topic.id],
                    )
                  }
                  className={clsx('chip transition', selected ? 'text-white' : 'text-ink/70')}
                  style={selected ? { backgroundColor: topic.color } : { backgroundColor: `${topic.color}1f` }}
                >
                  {topic.name}
                </button>
              );
            })}
          </div>
        </div>

        <div>
          <div className="flex items-center justify-between">
            <span className="text-sm font-medium">
              {linkedIds.length === 0
                ? 'No linked comments'
                : `${linkedIds.length} linked comment${linkedIds.length === 1 ? '' : 's'}`}
            </span>
            <button
              type="button"
              className="text-xs font-medium text-brand-600 hover:underline"
              onClick={() => setShowLinks((value) => !value)}
            >
              {showLinks ? 'Done' : 'Edit'}
            </button>
          </div>

          {showLinks ? (
            <ul className="mt-2 max-h-48 space-y-1 overflow-y-auto rounded-xl border border-black/10 p-2">
              {comments.map((comment) => {
                const selected = linkedIds.includes(comment.id);
                return (
                  <li key={comment.id}>
                    <label className="flex cursor-pointer items-start gap-2 rounded-lg p-1.5 text-sm hover:bg-black/[0.03]">
                      <input
                        type="checkbox"
                        className="mt-1"
                        checked={selected}
                        onChange={() =>
                          setLinkedIds((current) =>
                            selected ? current.filter((id) => id !== comment.id) : [...current, comment.id],
                          )
                        }
                      />
                      <span className="line-clamp-2">{comment.text}</span>
                    </label>
                  </li>
                );
              })}
            </ul>
          ) : (
            <p className="mt-1 rounded-xl bg-brand-50 px-3 py-2 text-xs text-ink/70">
              Linking the comments behind an action gives future-you the context for why it exists.
            </p>
          )}
        </div>
      </div>
    </Modal>
  );
}

function RowBody({ action }: { action: ActionItemView }) {
  return (
    <>
      <p className={clsx('text-sm font-medium', action.status === 'DONE' && 'line-through opacity-60')}>
        {action.title}
      </p>
      {action.description ? (
        <p className="mt-0.5 line-clamp-2 whitespace-pre-wrap text-sm text-ink/65">{action.description}</p>
      ) : null}
      {action.dueDate ? (
        <p className="mt-1 text-xs text-ink/55">Due {new Date(action.dueDate).toLocaleDateString()}</p>
      ) : null}
    </>
  );
}

export function ActionItemRow({
  action,
  onEdit,
  onToggleDone,
}: {
  action: ActionItemView;
  onEdit?: () => void;
  onToggleDone?: (done: boolean) => void;
}) {
  return (
    <li className="rounded-xl border border-black/10 p-3">
      <div className="flex items-start gap-2.5">
        {onToggleDone ? (
          <input
            type="checkbox"
            className="mt-1"
            checked={action.status === 'DONE'}
            onChange={(event) => onToggleDone(event.target.checked)}
            aria-label={`Mark “${action.title}” as done`}
          />
        ) : null}

        {onEdit ? (
          <button type="button" onClick={onEdit} className="min-w-0 flex-1 text-left">
            <RowBody action={action} />
          </button>
        ) : (
          <div className="min-w-0 flex-1">
            <RowBody action={action} />
          </div>
        )}

        {action.assignee ? (
          <Avatar emoji={action.assignee.avatar} name={action.assignee.name} size="sm" />
        ) : (
          <span
            className="mt-0.5 flex h-7 w-7 items-center justify-center rounded-full border border-dashed border-black/20 text-[10px] text-ink/40"
            title="No owner yet"
          >
            ?
          </span>
        )}
      </div>
    </li>
  );
}
